import { emptyString } from '@atlassian/amkt-common';
import { foldAuthType } from '@atlassian/mpac-types';
import { Either, Option } from 'funfix-core';
import { List, Record } from 'immutable';
import { CLOSE_SITE_UNINSTALL_DIALOG, FETCH_FRESH_INSTALLED_APPS, FETCH_MORE_GLOBALLY_INSTALLED_APPS, FETCH_MORE_LOCALLY_INSTALLED_APPS, FINISH_FETCH_MARKETPLACE_APP_DETAILS, FINISH_REVOKE_USER_GRANT_GLOBALLY, FINISH_UNINSTALL_APP_FROM_LOCAL_CONTEXT, FINISH_UNINSTALL_APP_FROM_SITE, OPEN_SITE_UNINSTALL_DIALOG, RECEIVE_FRESH_INSTALLED_APPS, RECEIVE_FRESH_INSTALLED_APPS_ERROR, RECEIVE_MORE_GLOBALLY_INSTALLED_APPS, RECEIVE_MORE_GLOBALLY_INSTALLED_APPS_ERROR, RECEIVE_MORE_LOCALLY_INSTALLED_APPS, RECEIVE_MORE_LOCALLY_INSTALLED_APPS_ERROR, REVOKE_USER_GRANT_GLOBALLY_ERROR, START_REVOKE_USER_GRANT_GLOBALLY, START_UNINSTALL_APP_FROM_LOCAL_CONTEXT, START_UNINSTALL_APP_FROM_SITE, UNINSTALL_APP_FROM_LOCAL_CONTEXT_ERROR, UNINSTALL_APP_FROM_SITE_ERROR } from '../actions/InstalledApps';
export const makeContextInstalledAppsResult = Record({
  installedApps: List(),
  loadingMoreResults: false,
  totalCount: 0,
  nextOffset: Option.empty()
});
export const makeContextInstalledApps = Record({
  result: Option.empty(),
  requestId: undefined
});
export const makeInstalledAppsState = Record({
  currentSearch: emptyString,
  locallyInstalledApps: makeContextInstalledApps(),
  globallyInstalledApps: makeContextInstalledApps(),
  uninstallingAppFromSite: Option.none()
});
const updateRevokeInProgressState = (state, appKey, userGrant, revokeInProgress) => state.updateIn(['globallyInstalledApps', 'result'], rr => rr.map(r => r.map(iar => iar.update('installedApps', gias => gias.map(gia => gia.details.key === appKey ? gia.update('authType', authType => foldAuthType(authType)(value => value, value => value, threeLeggedAuthType => threeLeggedAuthType.update('grants', gs => gs.map(g => g.user.atlassianAccountId === userGrant.user.atlassianAccountId ? g.set('revokeInProgress', revokeInProgress) : g)))) : gia)))));
const receiveFreshInstalledApps = (contextInstalledApps, requestId, installedAppsPage) => contextInstalledApps.requestId === requestId ? contextInstalledApps.delete('requestId').set('result', Option.some(Either.right(makeContextInstalledAppsResult({
  installedApps: installedAppsPage.values,
  totalCount: installedAppsPage.totalCount,
  nextOffset: installedAppsPage.nextOffset
})))) : contextInstalledApps;
const receiveFreshInstalledAppsError = (contextInstalledApps, requestId, dataErrorMessages) => contextInstalledApps.requestId === requestId ? contextInstalledApps.delete('requestId').set('result', Option.some(Either.left(dataErrorMessages))) : contextInstalledApps;
const fetchMoreInstalledApps = (contextInstalledApps, requestId) => contextInstalledApps.set('requestId', requestId).update('result', rr => rr.map(r => r.map(res => res.set('loadingMoreResults', true))));
const receiveMoreInstalledApps = (contextInstalledApps, requestId, installedAppsPage) => contextInstalledApps.requestId === requestId ? contextInstalledApps.delete('requestId').update('result', rr => rr.map(r => r.map(iar => iar.update('installedApps', ias => ias.concat(installedAppsPage.values)).set('nextOffset', installedAppsPage.nextOffset).set('loadingMoreResults', false)))) : contextInstalledApps;
const receiveMoreInstalledAppsError = (contextInstalledApps, requestId) => contextInstalledApps.requestId === requestId ? contextInstalledApps.delete('requestId').update('result', rr => rr.map(r => r.map(iar => iar.set('loadingMoreResults', false)))) : contextInstalledApps;

// eslint-disable-next-line complexity, max-lines-per-function -- Fix me
export default function InstalledAppsReducer() {
  let state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : makeInstalledAppsState();
  let action = arguments.length > 1 ? arguments[1] : undefined;
  switch (action.type) {
    case FETCH_FRESH_INSTALLED_APPS:
      return state.set('currentSearch', action.search).set('locallyInstalledApps', makeContextInstalledApps({
        requestId: action.requestId
      })).set('globallyInstalledApps', makeContextInstalledApps({
        requestId: action.requestId
      }));
    case RECEIVE_FRESH_INSTALLED_APPS:
      {
        const requestId = action.requestId;
        const locallyInstalledAppsPage = action.locallyInstalledAppsPage;
        const globallyInstalledAppsPage = action.globallyInstalledAppsPage;
        return state.update('locallyInstalledApps', lias => receiveFreshInstalledApps(lias, requestId, locallyInstalledAppsPage)).update('globallyInstalledApps', gias => receiveFreshInstalledApps(gias, requestId, globallyInstalledAppsPage));
      }
    case RECEIVE_FRESH_INSTALLED_APPS_ERROR:
      {
        const requestId = action.requestId;
        const dataErrorMessages = action.dataErrorMessages;
        return state.update('locallyInstalledApps', lias => receiveFreshInstalledAppsError(lias, requestId, dataErrorMessages)).update('globallyInstalledApps', gias => receiveFreshInstalledAppsError(gias, requestId, dataErrorMessages));
      }
    case FETCH_MORE_GLOBALLY_INSTALLED_APPS:
      {
        const requestId = action.requestId;
        return state.update('globallyInstalledApps', gias => fetchMoreInstalledApps(gias, requestId));
      }
    case RECEIVE_MORE_GLOBALLY_INSTALLED_APPS:
      {
        const requestId = action.requestId;
        const globallyInstalledAppsPage = action.globallyInstalledAppsPage;
        return state.update('globallyInstalledApps', gias => receiveMoreInstalledApps(gias, requestId, globallyInstalledAppsPage));
      }
    case RECEIVE_MORE_GLOBALLY_INSTALLED_APPS_ERROR:
      {
        const requestId = action.requestId;
        return state.update('globallyInstalledApps', gias => receiveMoreInstalledAppsError(gias, requestId));
      }
    case FETCH_MORE_LOCALLY_INSTALLED_APPS:
      {
        const requestId = action.requestId;
        return state.update('locallyInstalledApps', lias => fetchMoreInstalledApps(lias, requestId));
      }
    case RECEIVE_MORE_LOCALLY_INSTALLED_APPS:
      {
        const requestId = action.requestId;
        const locallyInstalledAppsPage = action.locallyInstalledAppsPage;
        return state.update('locallyInstalledApps', gias => receiveMoreInstalledApps(gias, requestId, locallyInstalledAppsPage));
      }
    case RECEIVE_MORE_LOCALLY_INSTALLED_APPS_ERROR:
      {
        const requestId = action.requestId;
        return state.update('locallyInstalledApps', lias => receiveMoreInstalledAppsError(lias, requestId));
      }
    case OPEN_SITE_UNINSTALL_DIALOG:
      return state.set('uninstallingAppFromSite', Option.some(action.installedApp));
    case CLOSE_SITE_UNINSTALL_DIALOG:
      return state.set('uninstallingAppFromSite', Option.none());
    case START_UNINSTALL_APP_FROM_LOCAL_CONTEXT:
      {
        const appKey = action.installedApp.details.key;
        return state.updateIn(['locallyInstalledApps', 'result'], rr => rr.map(r => r.map(iar => iar.update('installedApps', ias => ias.map(lia => lia.details.key === appKey ? lia.set('uninstallInProgress', true) : lia)))));
      }
    case FINISH_UNINSTALL_APP_FROM_LOCAL_CONTEXT:
      {
        const appKey = action.installedApp.details.key;
        return state.updateIn(['locallyInstalledApps', 'result'], rr => rr.map(r => r.map(iar => iar.update('installedApps', ias => ias.filter(ia => ia.details.key !== appKey)).update('totalCount', tc => tc - 1))));
      }
    case UNINSTALL_APP_FROM_LOCAL_CONTEXT_ERROR:
      {
        const appKey = action.installedApp.details.key;
        return state.updateIn(['locallyInstalledApps', 'result'], rr => rr.map(r => r.map(iar => iar.update('installedApps', ias => ias.map(lia => lia.details.key === appKey ? lia.set('uninstallInProgress', false) : lia)))));
      }
    case START_UNINSTALL_APP_FROM_SITE:
      return state.update('uninstallingAppFromSite', uia => uia.map(ia => ia.set('uninstallInProgress', true)));
    case FINISH_UNINSTALL_APP_FROM_SITE:
      {
        const appKey = action.installedApp.details.key;
        return state.updateIn(['locallyInstalledApps', 'result'], rr => rr.map(r => r.map(iar => iar.update('installedApps', ias => ias.filter(ia => ia.details.key !== appKey))))).updateIn(['globallyInstalledApps', 'result'], rr => rr.map(r => r.map(iar => iar.update('installedApps', ias => ias.filter(ia => ia.details.key !== appKey))))).set('uninstallingAppFromSite', Option.none());
      }
    case UNINSTALL_APP_FROM_SITE_ERROR:
      return state.update('uninstallingAppFromSite', uia => uia.map(ia => ia.set('uninstallInProgress', false)));
    case START_REVOKE_USER_GRANT_GLOBALLY:
      return updateRevokeInProgressState(state, action.installedApp.details.key, action.userGrant, true);
    case FINISH_REVOKE_USER_GRANT_GLOBALLY:
      {
        const appKey = action.installedApp.details.key;
        const userGrant = action.userGrant;
        return state.updateIn(['globallyInstalledApps', 'result'], rr => rr.map(r => r.map(iar => iar.update('installedApps', gias => gias.map(gia => gia.details.key === appKey ? gia.update('authType', authType => foldAuthType(authType)(value => value, value => value, threeLeggedAuthType => threeLeggedAuthType.update('grants', gs => gs.filter(g => g.user.atlassianAccountId !== userGrant.user.atlassianAccountId)))) : gia).filter(ia => foldAuthType(ia.authType)(() => true, () => true, threeLeggedAuthType => threeLeggedAuthType.grants.size > 0))))));
      }
    case REVOKE_USER_GRANT_GLOBALLY_ERROR:
      return updateRevokeInProgressState(state, action.installedApp.details.key, action.userGrant, false);
    case FINISH_FETCH_MARKETPLACE_APP_DETAILS:
      {
        const marketplaceAppDetails = action.marketplaceAppDetails;
        const appKey = marketplaceAppDetails.key;
        const updateContextName = action.isGlobalContext ? 'globallyInstalledApps' : 'locallyInstalledApps';
        return state.updateIn([updateContextName, 'result'], rr => rr.map(r => r.map(iar => iar.update('installedApps', ias => ias.map(ia => ia.details.key === appKey ? ia.set('marketplaceAppDetails', marketplaceAppDetails) : ia)))));
      }
    default:
      // Flow compile-time validation that we've handled every action
      // prettier-ignore
      action;
      return state;
  }
}